import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';

const TextType = ({
  text = '',
  as: Component = 'div',
  typingSpeed = 75,
  initialDelay = 0,
  pauseDuration = 1500,
  deletingSpeed = 50,
  loop = true,
  className = '',
  showCursor = true,
  hideCursorWhileTyping = false,
  cursorCharacter = '|',
  cursorBlinkDuration = 0.5,
  cursorClassName = '',
  textColors = [],
  variableSpeed,
  onSentenceComplete,
  startOnVisible = false,
  reverseMode = false
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [textIndex, setTextIndex] = useState(0);
  const cursorRef = useRef(null);
  const containerRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(!startOnVisible);

  const textsArray = Array.isArray(text) ? text : [text];
  const currentText = textsArray[textIndex];

  useEffect(() => {
    if (!startOnVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHasStarted(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [startOnVisible]);

  useEffect(() => {
    if (!hasStarted) return;

    let timeout;

    const getTypingSpeed = () => {
      if (variableSpeed) {
        return Math.random() * (variableSpeed.max - variableSpeed.min) + variableSpeed.min;
      }
      return typingSpeed;
    };

    if (!isTyping && !isDeleting && displayedText === '' && initialDelay > 0) {
      timeout = setTimeout(() => setIsTyping(true), initialDelay);
    } else if (isTyping) {
      if (displayedText.length < currentText.length) {
        timeout = setTimeout(() => {
          const nextChar = reverseMode
            ? currentText[currentText.length - displayedText.length - 1]
            : currentText[displayedText.length];
          setDisplayedText((prev) => (reverseMode ? nextChar + prev : prev + nextChar));
        }, getTypingSpeed());
      } else {
        setIsTyping(false);
        if (onSentenceComplete) {
          onSentenceComplete(currentText, textIndex);
        }
        if (textsArray.length > 1) {
          timeout = setTimeout(() => setIsDeleting(true), pauseDuration);
        }
      }
    } else if (isDeleting) {
      if (displayedText.length > 0) {
        timeout = setTimeout(() => {
          setDisplayedText((prev) => (reverseMode ? prev.slice(1) : prev.slice(0, -1)));
        }, deletingSpeed);
      } else {
        setIsDeleting(false);
        setTextIndex((prev) => (prev + 1) % textsArray.length);
        if (loop || textIndex + 1 < textsArray.length) {
          setIsTyping(true);
        }
      }
    } else if (!isTyping && !isDeleting && displayedText === '') {
      setIsTyping(true);
    }

    return () => clearTimeout(timeout);
  }, [displayedText, isTyping, isDeleting, currentText, textIndex, hasStarted]);

  useEffect(() => {
    if (showCursor && cursorRef.current) {
      gsap.fromTo(
        cursorRef.current,
        { opacity: 1 },
        {
          opacity: 0,
          duration: cursorBlinkDuration,
          repeat: -1,
          yoyo: true,
          ease: 'power1.inOut'
        }
      );
    }
  }, [showCursor, cursorBlinkDuration]);

  const textColor = textColors[textIndex] || '';
  const shouldShowCursor = showCursor && (!hideCursorWhileTyping || !isTyping);

  return (
    <Component ref={containerRef} className={className}>
      <span style={{ color: textColor }}>{displayedText}</span>
      {shouldShowCursor && (
        <span ref={cursorRef} className={cursorClassName}>
          {cursorCharacter}
        </span>
      )}
    </Component>
  );
};

export default TextType;